import { useMemo, useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { CircleHelpIcon } from 'lucide-react';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { FormField } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import i18n from '@/i18n/i18n';
import { randId } from '@/redux/dialogs/helper';
import {
  selectRequisitesByNodeId,
  selectSelectedNode,
  selectSelectedRequisiteIndex,
} from '@/redux/dialogs/selectors';
import { updateRequisite } from '@/redux/nodes/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { TextAction } from '@/shared/types/action';
import { Requisite, RequisiteOptionType } from '@/shared/types/node';
import { RootState } from '@/shared/types/state';
import { QUESTION_DOCS_URL } from '@/util/constants';
import { removeDollarSign, addDollarSign, capitalizeFirstLetter } from '@/util/util';
import { LENGTH_L } from '@/util/validation/constants';
import { contextRefSchema } from '@/util/validation/schema-builders';

import QuestionAskOnly from './QuestionAskOnly';
import QuestionCheckAndAsk from './QuestionCheckAndAsk';
import ToolkitWrapper from '../../ToolkitWrapper';

// ============================================================================
// Schema
// ============================================================================

const toolkitActionQuestionSchema = yup.object().shape({
  questionType: yup.string().oneOf(['ask', 'check_ask']),
  save_as: contextRefSchema
    .min(3)
    .max(LENGTH_L)
    .required(() => i18n.t('validation.required')),
  mandatory: yup.boolean().optional(),
});

// ============================================================================
// Types
// ============================================================================

type FormType = yup.InferType<typeof toolkitActionQuestionSchema>;

interface SaveAsOptionData {
  type: string;
}

// Constants
const QuestionType = { ASK_ONLY: 'ask', CHECK_AND_ASK: 'check_ask' } as const;

type CheckAndAskCache = {
  check_for?: string;
  required?: boolean;
  validate?: string;
  reprompt?: Omit<TextAction, 'action_id'>[];
  validation_guideline?: string;
  mandatory?: boolean;
};

const ToolkitActionQuestion = () => {
  // Translation and Redux hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Selectors
  const selectedNode = useSelector(selectSelectedNode);
  const nodeId = selectedNode?.node_id;
  const requisiteIndex = useSelector(selectSelectedRequisiteIndex);
  const brainId = useSelector(selectBrainId);
  const requisite: Requisite | undefined = useSelector(
    (state: RootState) => selectRequisitesByNodeId(state, nodeId)?.[requisiteIndex]
  );

  // Custom hooks
  const { getContextVariables } = useDialogs(brainId);

  // State for temporary cache of Check and Ask fields
  const [checkAndAskCache, setCheckAndAskCache] = useState<CheckAndAskCache | null>(null);

  // Memoized values
  const questionTypes = useMemo(
    () => [
      { value: QuestionType.ASK_ONLY, label: t('dialog.question.ask_only') },
      { value: QuestionType.CHECK_AND_ASK, label: t('dialog.question.check_and_ask') },
    ],
    [t]
  );

  const requisiteOptions: RequisiteOptionType[] = get(requisite, 'actions[0].options', []);

  /**
   * Context variables mapped to AutocompleteOption format with category for grouping
   * Filters out the tags variable
   */
  const options = useMemo((): AutocompleteOption<SaveAsOptionData>[] => {
    const contextVariables = getContextVariables().filter(
      (variable) => variable?.value !== '$tags'
    );

    return contextVariables.map((variable) => ({
      value: variable.value,
      label: variable.label,
      category: t(`auto_complete.group_labels.${variable.type}`),
      data: { type: variable.type },
    }));
  }, [getContextVariables, t]);

  /**
   * Default save_as value (string with dollar sign for display)
   */
  const defaultSaveAs = useMemo(
    () => addDollarSign(requisite?.save_as) || '',
    [requisite?.save_as]
  );

  // Form configuration
  const {
    trigger,
    control,
    formState: { errors },
    setValue,
  } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      questionType:
        requisite?.check_for ||
        requisite?.validate ||
        requisite?.validate === '' ||
        requisite?.validation_guideline ||
        requisite?.mandatory
          ? QuestionType.CHECK_AND_ASK
          : QuestionType.ASK_ONLY,
      save_as: defaultSaveAs,
      mandatory: requisite?.mandatory ?? false,
    },
    resolver: yupResolver(toolkitActionQuestionSchema) as Resolver<FormType>,
  });

  // Computed values
  const saveAsErrorMessage = capitalizeFirstLetter(errors?.save_as?.message);
  const watchQuestionType = useWatch({ control, name: 'questionType' });
  const watchMandatory = useWatch({ control, name: 'mandatory' });

  // Handler functions
  const handleRequisiteUpdate = useCallback(
    (updatedRequisite) => {
      dispatch(updateRequisite({ nodeId, index: requisiteIndex, requisite: updatedRequisite }));
    },
    [dispatch, nodeId, requisiteIndex]
  );

  const updateRedux = useCallback(
    (key: string, value: string) => {
      dispatch(
        updateRequisite({
          nodeId,
          index: requisiteIndex,
          requisite: { ...requisite, [key]: value },
        })
      );
    },
    [dispatch, nodeId, requisite, requisiteIndex]
  );

  const handleMandatoryChange = useCallback(
    (checked: boolean) => {
      setValue('mandatory', checked);
      dispatch(
        updateRequisite({
          nodeId,
          index: requisiteIndex,
          requisite: { ...requisite, mandatory: checked },
        })
      );
    },
    [dispatch, nodeId, requisite, requisiteIndex, setValue]
  );

  const { setFieldError: updateErrors } = useDialogAlert({
    id: requisite?.requisite_id,
    nodeId,
    title: t('actions.types.question'),
    type: 'question',
    index: requisiteIndex,
  });

  const handleAskOnlyChange = (requisite: Requisite) => {
    // Cache Check and Ask fields before removing them
    setCheckAndAskCache(requisite);

    // Use the existing texts if they exist, otherwise generate a random new one
    const texts = get(requisite, 'actions[0].texts', [t('actions.generate_text', { 0: randId() })]);
    return {
      ...requisite,
      check_for: undefined,
      required: undefined,
      validate: undefined,
      reprompt: undefined,
      validation_guideline: undefined,
      // Remove the mandatory field when switching to ask only
      mandatory: undefined,
      actions: [
        {
          type: 'text',
          texts: texts,
          options: requisiteOptions.length !== 0 ? requisiteOptions : undefined,
        } as TextAction,
      ],
    };
  };

  const handleCheckAndAskChange = (requisite: Requisite) => {
    // Check if we have cached values to restore
    const hasCache =
      checkAndAskCache && Object.values(checkAndAskCache).some((v) => v !== undefined);

    const fallbackText =
      requisite?.actions?.[0].texts?.[0] || t('actions.generate_text', { 0: randId() });
    const fallbackReprompt =
      requisite?.reprompt && requisite.reprompt[0]?.texts?.[0]
        ? requisite.reprompt
        : [
            {
              type: 'text',
              texts: [fallbackText],
              options: requisiteOptions.length !== 0 ? requisiteOptions : undefined,
            } as TextAction,
          ];

    return {
      ...requisite,
      // Restore from cache if available, otherwise use defaults
      check_for: hasCache ? checkAndAskCache.check_for : requisite?.check_for,
      required: hasCache ? checkAndAskCache.required : true,
      validate: hasCache ? checkAndAskCache.validate : requisite?.validate,
      reprompt: hasCache ? checkAndAskCache.reprompt : fallbackReprompt,
      validation_guideline: hasCache
        ? checkAndAskCache.validation_guideline
        : requisite?.validation_guideline,
      mandatory: hasCache ? checkAndAskCache.mandatory : requisite?.mandatory,
      actions: [
        {
          type: 'text',
          texts: [fallbackText],
          options: requisiteOptions.length !== 0 ? requisiteOptions : undefined,
        } as TextAction,
      ],
    };
  };

  /**
   * Handles changes to the save_as combobox selection
   * Updates both form state and Redux
   * @param value - Selected value string (with dollar sign)
   */
  const handleSaveAsChange = useCallback(
    (value: string) => {
      // Validate and trigger form update
      trigger('save_as');

      // Update Redux with the value without dollar sign
      updateRedux('save_as', removeDollarSign(value));
    },
    [trigger, updateRedux]
  );

  /**
   * Handles creating a new variable entry
   * When user types a new value that doesn't exist in options
   * @param value - The new value entered by the user
   */
  const handleCreateNew = useCallback(
    (value: string) => {
      // Add dollar sign prefix if not present for consistency
      const valueWithDollarSign = addDollarSign(value);

      // Update form value with the dollar sign prefix
      setValue('save_as', valueWithDollarSign, { shouldValidate: true });

      // Update Redux with the value without dollar sign
      updateRedux('save_as', removeDollarSign(valueWithDollarSign));
    },
    [setValue, updateRedux]
  );

  // Effects
  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    updateErrors('save_as', saveAsErrorMessage);
  }, [saveAsErrorMessage, updateErrors]);

  if (!requisite) {
    return null;
  }

  return (
    <ToolkitWrapper type="question">
      <div className="mb-6">
        <FormField
          control={control}
          name="questionType"
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);

                let updatedRequisite = cloneDeep(requisite);
                if (value === QuestionType.ASK_ONLY) {
                  updatedRequisite = handleAskOnlyChange(updatedRequisite);
                  updateErrors('Question', undefined);
                  updateErrors('Reprompt', undefined);
                  setValue('mandatory', false);
                } else {
                  updatedRequisite = handleCheckAndAskChange(updatedRequisite);
                  updateErrors(t('dialog.question.ask'), undefined);
                }
                handleRequisiteUpdate(updatedRequisite);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {questionTypes.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {watchQuestionType === QuestionType.CHECK_AND_ASK && (
        <div className="mb-6">
          <div className="flex items-start gap-2 align-middle">
            <Checkbox
              id="mandatory"
              checked={watchMandatory}
              onCheckedChange={handleMandatoryChange}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-1">
              <Label htmlFor="mandatory" className="cursor-pointer text-sm font-medium">
                {t('dialog.question.mandatory_label')}
              </Label>
              <p className="text-sm text-foreground-muted">
                {t('dialog.question.mandatory_description')}
              </p>
            </div>
          </div>
        </div>
      )}

      {watchQuestionType === QuestionType.ASK_ONLY ? (
        <QuestionAskOnly
          requisite={requisite}
          options={options}
          updateErrors={updateErrors}
          nodeId={nodeId}
          requisiteIndex={requisiteIndex}
        />
      ) : (
        <QuestionCheckAndAsk
          requisite={requisite}
          onRequisiteUpdate={handleRequisiteUpdate}
          options={options}
          updateErrors={updateErrors}
        />
      )}

      <div className="mt-6">
        <FormAutocomplete
          name="save_as"
          control={control}
          options={options}
          label={
            <label className="mb-1 flex items-center gap-1 text-sm leading-5 font-normal text-foreground">
              {t('dialog.question.variable_label')}
              <InfoTooltip
                content={t('dialog.question.variable_tooltip')}
                learnMoreUrl={QUESTION_DOCS_URL}
                side="right"
              >
                <CircleHelpIcon size={12} className="text-foreground-muted" />
              </InfoTooltip>
            </label>
          }
          placeholder={t('dialog.set_variables.variable_name.placeholder')}
          onChange={handleSaveAsChange}
          onCreateNew={handleCreateNew}
          error={!!errors?.save_as}
          errorMessage={saveAsErrorMessage}
        />
      </div>
    </ToolkitWrapper>
  );
};

export default ToolkitActionQuestion;
